import React from 'react';
import styles from 'components/forms/CompanyForm/View/styles.module.scss';

export interface OnboardingRow {
  name: React.ReactNode;
  value: React.ReactNode;
  hasEditIcon?: boolean;
}

export interface OnboardingDataResult {
  rows: OnboardingRow[];
  dontKnowCount: number;
}

interface OnboardingData {
  app_questionary: {
    distribution: {
      appleStore?: boolean;
      appleStoreComment?: string;
      googleStore?: boolean;
      googleStoreComment?: string;
      other?: boolean;
      otherComment?: string;
      steam?: boolean;
      steamComment?: string;
    };
    monetization?: string | string[];
    uaSpend?: {
      appleSearchAds?: boolean;
      dontKnow?: boolean;
      facebook?: boolean;
      googleAds?: boolean;
      other?: boolean;
      otherComment?: string;
      snapchat?: boolean;
      tikTok?: boolean;
      unityAds?: boolean;
    };
    mmp?: {
      adjust?: boolean;
      appsflyer?: boolean;
      branch?: boolean;
      dontKnow?: boolean;
      firebase?: boolean;
      kochava?: boolean;
      other?: boolean;
      otherComment?: string;
      tenjin?: boolean;
    };
    additionalInfo?: string;
  };
}

const DONT_KNOW_TEXT = "I don't know";
const DONT_KNOW_COLOR = '#EC3E72';

const renderWithDontKnowHighlight = (items: string[]) => (
  <span className={styles.value}>
    {items.map((item, index) => (
      <span key={index}>
        {item === DONT_KNOW_TEXT ? <span style={{ color: DONT_KNOW_COLOR }}>{item}</span> : item}
        {index < items.length - 1 ? ', ' : ''}
      </span>
    ))}
  </span>
);

const mapDistribution = (distribution: OnboardingData['app_questionary']['distribution']): OnboardingRow[] => {
  const rows: OnboardingRow[] = [];

  if (distribution.appleStore && distribution.appleStoreComment && distribution.appleStoreComment.length > 0) {
    rows.push({
      name: <span className={styles.label}>Apple Appstore</span>,
      value: (
        <a href={distribution.appleStoreComment} className={styles.link} target="_blank" rel="noreferrer">
          {distribution.appleStoreComment}
        </a>
      ),
      hasEditIcon: false,
    });
  }

  if (distribution.googleStore && distribution.googleStoreComment && distribution.googleStoreComment.length > 0) {
    rows.push({
      name: <span className={styles.label}>Google Play</span>,
      value: (
        <a href={distribution.googleStoreComment} className={styles.link} target="_blank" rel="noreferrer">
          {distribution.googleStoreComment}
        </a>
      ),
      hasEditIcon: false,
    });
  }

  if (distribution.other && distribution.otherComment && distribution.otherComment.length > 0) {
    rows.push({
      name: <span className={styles.label}>Other Store</span>,
      value: (
        <a href={distribution.otherComment} className={styles.link} target="_blank" rel="noreferrer">
          {distribution.otherComment}
        </a>
      ),
      hasEditIcon: false,
    });
  }

  if (distribution.steam && distribution.steamComment && distribution.steamComment.length > 0) {
    rows.push({
      name: <span className={styles.label}>Steam Store</span>,
      value: (
        <a href={distribution.steamComment} className={styles.link} target="_blank" rel="noreferrer">
          {distribution.steamComment}
        </a>
      ),
      hasEditIcon: false,
    });
  }

  return rows;
};

const mapMonetization = (monetization: string | string[] | undefined): { row: OnboardingRow; hasDontKnow: boolean } => {
  if (!monetization) {
    return {
      row: {
        name: <span className={styles.label}>Monetization model of your app/game?</span>,
        value: <span className={styles.value}>-</span>,
        hasEditIcon: false,
      },
      hasDontKnow: false,
    };
  }

  const isArray = Array.isArray(monetization);
  const hasIDontKnow = isArray ? monetization.includes(DONT_KNOW_TEXT) : monetization === DONT_KNOW_TEXT;

  return {
    row: {
      name: <span className={styles.label}>Monetization model of your app/game?</span>,
      value: (
        <span className={styles.value}>
          {isArray
            ? monetization.map((item, index) => (
                <span key={index}>
                  {item === DONT_KNOW_TEXT ? <span style={{ color: DONT_KNOW_COLOR }}>{item}</span> : item}
                  {index < monetization.length - 1 ? ', ' : ''}
                </span>
              ))
            : monetization}
        </span>
      ),
      hasEditIcon: false,
    },
    hasDontKnow: hasIDontKnow,
  };
};

const mapUaSpend = (uaSpend: OnboardingData['app_questionary']['uaSpend']): { row: OnboardingRow; hasDontKnow: boolean } => {
  if (!uaSpend) {
    return {
      row: {
        name: <span className={styles.label}>UA spends sources:</span>,
        value: <span className={styles.value}>-</span>,
        hasEditIcon: false,
      },
      hasDontKnow: false,
    };
  }

  const items: string[] = [];
  let hasDontKnow = false;

  if (uaSpend.appleSearchAds) items.push('Apple Ads');
  if (uaSpend.dontKnow) {
    items.push(DONT_KNOW_TEXT);
    hasDontKnow = true;
  }
  if (uaSpend.facebook) items.push('Facebook');
  if (uaSpend.googleAds) items.push('Google Ads');
  if (uaSpend.other) {
    if (uaSpend.otherComment) {
      items.push(`Other: ${uaSpend.otherComment}`);
    } else {
      items.push('Other');
    }
  }
  if (uaSpend.snapchat) items.push('Snapchat');
  if (uaSpend.tikTok) items.push('TikTok');
  if (uaSpend.unityAds) items.push('UnityAds');

  return {
    row: {
      name: <span className={styles.label}>UA spends sources:</span>,
      value: items.length > 0 ? renderWithDontKnowHighlight(items) : <span className={styles.value}>-</span>,
      hasEditIcon: false,
    },
    hasDontKnow,
  };
};

const mapMmp = (mmp: OnboardingData['app_questionary']['mmp']): { row: OnboardingRow; hasDontKnow: boolean } => {
  if (!mmp) {
    return {
      row: {
        name: <span className={styles.label}>Your current MMP (tracking service):</span>,
        value: <span className={styles.value}>-</span>,
        hasEditIcon: false,
      },
      hasDontKnow: false,
    };
  }

  const items: string[] = [];
  let hasDontKnow = false;

  if (mmp.adjust) items.push('Adjust');
  if (mmp.appsflyer) items.push('Appsflyer');
  if (mmp.branch) items.push('Branch');
  if (mmp.dontKnow) {
    items.push(DONT_KNOW_TEXT);
    hasDontKnow = true;
  }
  if (mmp.firebase) items.push('Firebase');
  if (mmp.kochava) items.push('Kochava');
  if (mmp.other) {
    if (mmp.otherComment) {
      items.push(`Other: ${mmp.otherComment}`);
    } else {
      items.push('Other');
    }
  }
  if (mmp.tenjin) items.push('Tenjin');

  return {
    row: {
      name: <span className={styles.label}>Your current MMP (tracking service):</span>,
      value: items.length > 0 ? renderWithDontKnowHighlight(items) : <span className={styles.value}>-</span>,
      hasEditIcon: false,
    },
    hasDontKnow,
  };
};

const mapAdditionalInfo = (additionalInfo: string | undefined): OnboardingRow => {
  return {
    name: <span className={styles.label}>Additional info about the project:</span>,
    value:
      additionalInfo && additionalInfo.length > 0 ? (
        <span className={styles.value}>{additionalInfo}</span>
      ) : (
        <span className={styles.value}>no comments</span>
      ),
    hasEditIcon: false,
  };
};

export const mapOnboardingData = (data: OnboardingData): OnboardingDataResult => {
  const rows: OnboardingRow[] = [];
  let dontKnowCount = 0;

  // 1. Distribution
  rows.push(...mapDistribution(data.app_questionary.distribution));

  // 2. Monetization
  const monetizationResult = mapMonetization(data.app_questionary.monetization);
  rows.push(monetizationResult.row);
  if (monetizationResult.hasDontKnow) dontKnowCount++;

  // 3. UA Spend
  const uaSpendResult = mapUaSpend(data.app_questionary.uaSpend);
  rows.push(uaSpendResult.row);
  if (uaSpendResult.hasDontKnow) dontKnowCount++;

  // 4. MMP
  const mmpResult = mapMmp(data.app_questionary.mmp);
  rows.push(mmpResult.row);
  if (mmpResult.hasDontKnow) dontKnowCount++;

  // 5. Additional Info
  rows.push(mapAdditionalInfo(data.app_questionary.additionalInfo));

  return {
    rows,
    dontKnowCount,
  };
};
